import { Download, Icon, RefreshCw, Search } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  type DataGridSort,
  DescriptionList,
  Drawer,
  EmptyState,
  FilterSelect,
  type FilterSelectOption,
  Freshness,
  Input,
  paginate,
  Pagination,
  Skeleton,
  Toggle,
  ToggleGroup,
} from "@norican/react";
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router";

import { control, subText } from "@/components/table";
import {
  type AdExportRequest,
  type OrgPerson,
  type SnapshotInfo,
  useExportOrgAd,
  useOrgSnapshotAll,
  useRefreshOrgSnapshot,
} from "@/lib/api";
import { BROWSER_TZ, errorMessage, inZone, zoneLabel } from "@/lib/format";
import { logger } from "@/lib/logger";

/**
 * Organisation → Active Directory: every person's work fields in one table,
 * as Entra ID has them. Timo, 4 Nov 2026: "basically an lookup in ad … with
 * an option to filter sort search well, and also a way to export it into
 * excel … it should be readonly" (docs/features/active-directory-view.md).
 *
 * **Read-only by construction.** The page reads `GET /org/snapshot?include=all`
 * and the only other call it makes is the export POST, which reads the same
 * snapshot. Refresh is the existing re-read of the directory. Nothing here
 * edits a value: a wrong one is corrected through Workforce → Report a change,
 * which carries the approval and the AD write, and the drawer links there.
 *
 * **Filtered here, once.** The browser holds every row (8 217 members on
 * 4 Nov 2026), scopes, searches, filters and sorts the whole set, and hands
 * the grid one page of 100: DataGrid renders every row it is given, so paging
 * is what keeps the DOM small. The export sends the resulting ids in screen
 * order, so the workbook is the list on screen and the filter logic exists in
 * this file only.
 *
 * **The list is the URL.** Search, filters, scope, gaps, sort and page sit in
 * the query string next to `view=ad`, so a bookmark or a link sent to a
 * colleague reproduces it. Column visibility is a per-browser preference and
 * lives in localStorage instead.
 */

// ─── columns ─────────────────────────────────────────────────────────────────

/** The export's column ids, minus `upn`, which the grid shows under the name. */
type ColumnId = Exclude<AdExportRequest["columns"][number], "upn">;

const COLUMNS: ReadonlyArray<{ id: ColumnId; label: string }> = [
  { id: "name", label: "Name" },
  { id: "supervisor", label: "Supervisor" },
  { id: "job_title", label: "Job title / function" },
  { id: "department", label: "Department" },
  { id: "business_category", label: "Area (business unit)" },
  { id: "country", label: "Country" },
  { id: "city", label: "City" },
  { id: "company_name", label: "Legal entity" },
  { id: "office_location", label: "Office (brand)" },
  { id: "account", label: "Account" },
  { id: "source", label: "Source" },
];
const COLUMN_LABEL = Object.fromEntries(COLUMNS.map((c) => [c.id, c.label])) as Record<
  ColumnId,
  string
>;
const isColumnId = (v: unknown): v is ColumnId => COLUMNS.some((c) => c.id === v);

const PAGE_SIZE = 100;
const DEFAULT_SORT: DataGridSort = { columnId: "name", direction: "asc" };
/** Thirty minutes is the Entra Connect cycle; past it the dot turns amber. */
const STALE_AFTER_MS = 30 * 60 * 1000;

/** One row of the table: the person, their supervisor's row, and what search reads. */
interface Row {
  id: string;
  p: OrgPerson;
  /** The supervisor's own row, when the snapshot holds it. */
  sup: OrgPerson | undefined;
  /** Name, UPN, title, department, supervisor's name and city, folded. */
  text: string;
  /**
   * Any of the six fields of spec §4 empty (supervisor, title, department,
   * country, city, legal entity), or a supervisor who is disabled. Not Area:
   * it is empty on 784 of 2 094 people (4 Nov 2026), more than the 574 gaps
   * of the six fields together; the Area filter's "(empty)" finds those.
   */
  gap: boolean;
}

const blank = (v: string | null | undefined): boolean => v == null || v.trim() === "";
/** A field as it comes, or null when empty, which the grid shows as a dash. */
const shown = (v: string | null | undefined): string | null => (blank(v) ? null : (v as string));
const nameOf = (p: OrgPerson | undefined): string => p?.display_name || p?.upn || "";

/**
 * The value each column sorts by. The grid gets the same functions as its
 * `accessor`s, and re-sorts the page it is handed by them: with one set of
 * functions and the grid's own comparison (below) that re-sort is a no-op,
 * so the order on screen is exactly the order the export sends.
 */
const ACCESSOR: Record<ColumnId, (r: Row) => string | null> = {
  name: (r) => shown(nameOf(r.p)),
  supervisor: (r) => shown(nameOf(r.sup)),
  job_title: (r) => shown(r.p.job_title),
  department: (r) => shown(r.p.department),
  business_category: (r) => shown(r.p.business_category),
  country: (r) => shown(r.p.country),
  city: (r) => shown(r.p.city),
  company_name: (r) => shown(r.p.company_name),
  office_location: (r) => shown(r.p.office_location),
  account: (r) => (r.p.account_enabled === false ? "Disabled" : "Enabled"),
  source: (r) => (r.p.synced ? "AD-synced" : "Cloud-only"),
};

/** DataGrid's comparison (DataGrid.js, @norican/react 2.0.1): empty last either way. */
function compareCells(a: string | null, b: string | null, dir: 1 | -1): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return a.localeCompare(b) * dir;
}

// ─── search ──────────────────────────────────────────────────────────────────

// Letters NFD does not split into base + accent. Norican spans DK, CZ, PL and
// DE: "Jorgensen" has to find "Jørgensen" just as "Pribram" finds "Příbram".
const LETTERS: Record<string, string> = {
  ø: "o",
  æ: "ae",
  œ: "oe",
  ß: "ss",
  ł: "l",
  đ: "d",
  ð: "d",
  þ: "th",
  ı: "i",
};

/** Lower case, accents off: what both sides of a search are compared as. */
function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[øæœßłđðþı]/g, (c) => LETTERS[c] ?? c);
}

/**
 * Every word must appear somewhere. Display names are "Surname, Given", so a
 * plain substring match would never find "Peter Hansen" in "Hansen, Peter".
 */
function searchTerms(q: string): string[] {
  return fold(q)
    .split(/[\s,]+/)
    .filter(Boolean);
}

// ─── scope, filters, URL state ───────────────────────────────────────────────

type Scope = "people" | "disabled" | "all";
const SCOPES: ReadonlyArray<{ id: Scope; label: string; summary: string }> = [
  {
    id: "people",
    label: "People",
    summary: "People (enabled accounts; no mailboxes or service accounts)",
  },
  { id: "disabled", label: "+ disabled", summary: "People, disabled accounts included" },
  {
    id: "all",
    label: "+ mailboxes and service accounts",
    summary: "Every account, mailboxes and service accounts included",
  },
];
const isScope = (v: string | null): v is Scope => SCOPES.some((s) => s.id === v);

/** The chart's `is_person` rule, served as `person`, plus the account state. */
function inScope(scope: Scope, p: OrgPerson): boolean {
  if (scope === "all") {
    return true;
  }
  if (p.person === false) {
    return false;
  }
  return scope === "disabled" || p.account_enabled !== false;
}

type FacetKey = "entity" | "country" | "city" | "department" | "area" | "supervisor";
/** An option for "this field is empty". Not a value any field can hold. */
const EMPTY = "__empty__";
const facetValue = (v: string | null | undefined): string => (blank(v) ? EMPTY : (v as string));

/**
 * The filters. Options come from the data, not from `vocab.py`, because the
 * data holds the spellings HR needs to find (52 legal-entity spellings for
 * about 20 entities, 4 Nov 2026). `key` is the URL parameter.
 */
const FACETS: ReadonlyArray<{ key: FacetKey; label: string; value: (r: Row) => string }> = [
  { key: "entity", label: "Legal entity", value: (r) => facetValue(r.p.company_name) },
  { key: "country", label: "Country", value: (r) => facetValue(r.p.country) },
  { key: "city", label: "City", value: (r) => facetValue(r.p.city) },
  { key: "department", label: "Department", value: (r) => facetValue(r.p.department) },
  { key: "area", label: "Area", value: (r) => facetValue(r.p.business_category) },
  { key: "supervisor", label: "Supervisor", value: (r) => r.p.manager_id || EMPTY },
];

interface ListState {
  q: string;
  scope: Scope;
  gaps: boolean;
  /** As in the URL; null is the default, Name ascending. */
  sortParam: DataGridSort | null;
  sort: DataGridSort;
  page: number;
  filters: Record<FacetKey, string[]>;
}

function readSort(v: string | null): DataGridSort | null {
  const [col, dir] = (v ?? "").split(":");
  return isColumnId(col) && (dir === "asc" || dir === "desc")
    ? { columnId: col, direction: dir }
    : null;
}

function readState(params: URLSearchParams): ListState {
  const scope = params.get("scope");
  const sortParam = readSort(params.get("sort"));
  return {
    q: params.get("q") ?? "",
    scope: isScope(scope) ? scope : "people",
    gaps: params.get("gaps") === "1",
    sortParam,
    sort: sortParam ?? DEFAULT_SORT,
    page: Number(params.get("page") ?? "1"),
    filters: Object.fromEntries(FACETS.map((f) => [f.key, params.getAll(f.key)])) as Record<
      FacetKey,
      string[]
    >,
  };
}

/** What Reset clears: search, filters, scope, gaps and page. Sort and columns stay. */
const LIST_PARAMS = ["q", "scope", "gaps", "page", ...FACETS.map((f) => f.key)];

// ─── column visibility ───────────────────────────────────────────────────────

// The hidden ids, not the shown ones: a column added later appears by default
// instead of staying invisible to everyone who once chose. Wrapped in
// try/catch like the theme choice (lib/theme.ts): a locked-down profile throws
// on access, and a column preference is never worth a blank screen.
const COLUMNS_KEY = "hrops.adview.columns";

function readHidden(): ColumnId[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter(isColumnId).filter((id) => id !== "name") : [];
  } catch {
    return [];
  }
}

function writeHidden(ids: ColumnId[]) {
  try {
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(ids));
  } catch {
    // Private window, or site data blocked: the choice lasts until reload.
  }
}

// ─── formatting ──────────────────────────────────────────────────────────────

const fmt = (n: number) => new Intl.NumberFormat("en-GB").format(n);

/** "4 Nov 2026, 14:05 Copenhagen": the viewer's zone, named, as lib/format asks. */
function asOf(iso: string): string {
  const { time, zone } = inZone(iso, BROWSER_TZ);
  return `${time} ${zoneLabel(zone)}`;
}

/** Re-render on a timer, so "12 min ago" and the dot's tone stay true. */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(t);
  }, [everyMs]);
  return now;
}

/** Workforce → Report a change, on the org-change form, with this person filled in. */
function reportLink(p: OrgPerson): string {
  const q = new URLSearchParams({
    view: "report",
    situation: "org_change",
    upn: p.upn ?? "",
    name: p.display_name ?? "",
  });
  return `/workforce?${q.toString()}`;
}

// ─── the view ────────────────────────────────────────────────────────────────

export function AdView({ enabled, mayReport }: { enabled: boolean; mayReport: boolean }) {
  const query = useOrgSnapshotAll(enabled);

  if (query.isPending) {
    return (
      <div className="mt-6 space-y-2" aria-busy="true" aria-label="Loading the directory">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="rect" height={32} />
        {Array.from({ length: 10 }, (_, i) => (
          <Skeleton key={i} variant="rect" height={24} />
        ))}
      </div>
    );
  }
  // Only when there is nothing to show. A failed background poll keeps the
  // last good data in TanStack v5, and the page keeps showing it (a deploy
  // restarting the API must not close HR's drawer and filtered list for five
  // minutes); AdTable says the re-read failed and Freshness ages the dot.
  if (query.error && !query.data) {
    return (
      <Alert tone="danger" title="Could not load the directory" className="mt-6">
        {errorMessage(query.error)}
      </Alert>
    );
  }
  const snap = query.data?.snapshot;
  if (!snap) {
    return (
      <EmptyState
        className="mt-6"
        title="No directory snapshot yet"
        description="HROps reads Entra ID every 15 minutes. Refresh reads it now."
        actions={<RefreshButton />}
      />
    );
  }
  return (
    <AdTable
      people={query.data?.people ?? []}
      snap={snap}
      mayReport={mayReport}
      rereadFailed={query.isRefetchError}
    />
  );
}

/** The existing re-read of the directory; it stores a snapshot only if something changed. */
function RefreshButton() {
  const refresh = useRefreshOrgSnapshot();
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        loading={refresh.isPending}
        startIcon={<Icon icon={RefreshCw} size="sm" />}
        onClick={() => {
          logger.debug("[org-ad]", "refresh");
          refresh.mutate();
        }}
      >
        {refresh.isPending ? "Reading Entra ID…" : "Refresh"}
      </Button>
      {refresh.error ? (
        <span className="text-danger-fg text-xs">
          {errorMessage(refresh.error, "Could not read the directory.")}
        </span>
      ) : refresh.data ? (
        <span className={subText}>
          {refresh.data.changed ? "Read again: new values stored." : "Read again: no change."}
        </span>
      ) : null}
    </span>
  );
}

function AdTable({
  people,
  snap,
  mayReport,
  rereadFailed,
}: {
  people: OrgPerson[];
  snap: SnapshotInfo;
  mayReport: boolean;
  /** The last background poll failed; the rows are the last good ones. */
  rereadFailed: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const st = useMemo(() => readState(params), [params]);
  // The search box keeps its own text. React Router renders a URL change as
  // a transition, so a box bound to the URL alone shows the old text until
  // that render commits, and keystrokes typed meanwhile are lost (seen on
  // 4 Nov 2026: typing "pri" left q=i). While the box has focus it is the
  // source; otherwise the URL is (Reset, a nav click, a pasted link).
  const [qInput, setQInput] = useState(st.q);
  const searchBox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (document.activeElement !== searchBox.current) {
      setQInput(st.q);
    }
  }, [st.q]);
  const [hidden, setHiddenState] = useState<ColumnId[]>(readHidden);
  const [selected, setSelected] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const top = useRef<HTMLDivElement>(null);
  const exportAd = useExportOrgAd();
  const now = useNow(30_000);

  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const reports = useMemo(() => {
    const out = new Map<string, OrgPerson[]>();
    for (const p of people) {
      if (p.manager_id) {
        const list = out.get(p.manager_id);
        if (list) {
          list.push(p);
        } else {
          out.set(p.manager_id, [p]);
        }
      }
    }
    return out;
  }, [people]);
  const rows = useMemo<Row[]>(
    () =>
      people.map((p) => {
        const sup = p.manager_id ? byId.get(p.manager_id) : undefined;
        return {
          id: p.id,
          p,
          sup,
          text: fold(
            [p.display_name, p.upn, p.job_title, p.department, sup?.display_name, p.city]
              .filter(Boolean)
              .join("\n"),
          ),
          gap:
            !p.manager_id ||
            sup?.account_enabled === false ||
            [p.job_title, p.department, p.country, p.city, p.company_name].some(blank),
        };
      }),
    [people, byId],
  );

  const labelOf = (key: FacetKey, v: string): string => {
    if (v === EMPTY) {
      return key === "supervisor" ? "(none)" : "(empty)";
    }
    if (key === "supervisor") {
      return nameOf(byId.get(v)) || "unknown account";
    }
    return v;
  };

  // Scope, then search, gaps and filters. Each filter's counts are taken with
  // every other filter applied but not itself, so an option says how many rows
  // picking it would add, and an option already picked keeps its count.
  const { base, filtered, options } = useMemo(() => {
    const terms = searchTerms(st.q);
    const active = FACETS.map((f) => [f, new Set(st.filters[f.key])] as const).filter(
      ([, s]) => s.size > 0,
    );
    const passes = (r: Row, except: FacetKey | null) =>
      (!st.gaps || r.gap) &&
      terms.every((t) => r.text.includes(t)) &&
      active.every(([f, s]) => f.key === except || s.has(f.value(r)));
    const inBase = rows.filter((r) => inScope(st.scope, r.p));
    const counted = Object.fromEntries(
      FACETS.map((f) => {
        const counts = new Map<string, number>(st.filters[f.key].map((v) => [v, 0]));
        for (const r of inBase) {
          const v = f.value(r);
          counts.set(v, (counts.get(v) ?? 0) + (passes(r, f.key) ? 1 : 0));
        }
        return [f.key, counts];
      }),
    ) as Record<FacetKey, Map<string, number>>;
    return {
      base: inBase,
      filtered: inBase.filter((r) => passes(r, null)),
      options: counted,
    };
  }, [rows, st]);

  const sorted = useMemo(() => {
    const by = ACCESSOR[st.sort.columnId as ColumnId] ?? ACCESSOR.name;
    const dir = st.sort.direction === "asc" ? 1 : -1;
    // Ties fall back to name, then id, so a page boundary never moves between
    // two renders of the same data.
    return [...filtered].sort(
      (x, y) =>
        compareCells(by(x), by(y), dir) ||
        nameOf(x.p).localeCompare(nameOf(y.p)) ||
        x.id.localeCompare(y.id),
    );
  }, [filtered, st.sort]);

  const range = paginate({ total: sorted.length, page: st.page, pageSize: PAGE_SIZE });
  const pageRows = sorted.slice(range.offset, range.offset + range.limit);

  // ── URL writes. `replace`, so typing a search is not twenty history entries.
  // Built on the address bar as it is now, not on setParams' `prev`: that is
  // the URL of the last committed render, and two writes before a commit
  // would drop the first.
  const update = (change: (n: URLSearchParams) => void, keepPage = false) =>
    setParams(
      () => {
        const n = new URLSearchParams(window.location.search);
        change(n);
        if (!keepPage) {
          n.delete("page");
        }
        return n;
      },
      { replace: true },
    );
  const setList = (key: string, values: string[]) =>
    update((n) => {
      n.delete(key);
      values.forEach((v) => n.append(key, v));
    });
  const resettable = LIST_PARAMS.some((k) => params.has(k));
  const reset = () => {
    setQInput("");
    update((n) => LIST_PARAMS.forEach((k) => n.delete(k)));
  };

  const setHidden = (ids: ColumnId[]) => {
    setHiddenState(ids);
    writeHidden(ids);
  };
  const visible = COLUMNS.filter((c) => !hidden.includes(c.id)).map((c) => c.id);

  const open = useCallback((id: string) => {
    setSelected(id);
    setDrawerOpen(true);
  }, []);
  // DataGrid has no row-click callback, so one handler on the wrapper reads
  // the row's id off its Name button. Clicks on a button or link inside the
  // row, and a click that ends a text selection, are left alone.
  const onGridClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("button, a") || window.getSelection()?.toString()) {
      return;
    }
    const id = target.closest("tbody tr")?.querySelector<HTMLElement>("[data-ad-person]")
      ?.dataset.adPerson;
    if (id) {
      open(id);
    }
  };

  const columns = useMemo<DataGridColumn<Row>[]>(
    () =>
      COLUMNS.map(({ id, label }) => ({
        id,
        header: label,
        sortable: true,
        accessor: ACCESSOR[id],
        ...(id === "name"
          ? {
              pinned: "left" as const,
              cell: (r: Row) => (
                <div>
                  <button
                    type="button"
                    data-ad-person={r.id}
                    className="text-fg text-left font-medium hover:underline"
                    onClick={() => open(r.id)}
                  >
                    {nameOf(r.p)}
                  </button>
                  <div className={subText}>{r.p.upn}</div>
                </div>
              ),
            }
          : id === "supervisor"
            ? { cell: (r: Row) => <SupervisorCell row={r} /> }
            : id === "account"
              ? {
                  cell: (r: Row) =>
                    r.p.account_enabled === false ? (
                      <Badge tone="warning" variant="subtle" size="sm">
                        Disabled
                      </Badge>
                    ) : (
                      "Enabled"
                    ),
                }
              : {}),
      })),
    [open],
  );

  const summary = (): string => {
    const parts = [SCOPES.find((s) => s.id === st.scope)?.summary ?? st.scope];
    if (st.q.trim()) {
      parts.push(`Search "${st.q.trim()}"`);
    }
    for (const f of FACETS) {
      const sel = st.filters[f.key];
      if (sel.length) {
        parts.push(`${f.label}: ${sel.map((v) => labelOf(f.key, v)).join(", ")}`);
      }
    }
    if (st.gaps) {
      parts.push("Gaps only");
    }
    parts.push(
      `Sorted by ${COLUMN_LABEL[st.sort.columnId as ColumnId] ?? st.sort.columnId}, ${
        st.sort.direction === "asc" ? "ascending" : "descending"
      }`,
    );
    return `${parts.join(". ")}.`.slice(0, 2000);
  };

  const runExport = () => {
    const cols = visible.flatMap((c): AdExportRequest["columns"] =>
      c === "name" ? ["name", "upn"] : [c],
    );
    logger.debug("[org-ad]", "export", { rows: sorted.length, columns: cols });
    exportAd.mutate({
      ids: sorted.map((r) => r.id),
      columns: cols,
      filter_summary: summary(),
      snapshot_id: snap.id,
    });
  };

  const peopleCount = useMemo(() => rows.filter((r) => inScope("people", r.p)).length, [rows]);
  const noArea = useMemo(() => !people.some((p) => !blank(p.business_category)), [people]);
  const person = selected ? byId.get(selected) : undefined;

  return (
    <section className="mt-6" ref={top}>
      <div className="flex flex-wrap items-center gap-3">
        <Freshness
          label={`Entra ID as of ${asOf(snap.checked_at)} · ${fmt(peopleCount)} people`}
          since={snap.checked_at}
          staleAfter={STALE_AFTER_MS}
          now={now}
        />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <RefreshButton />
          <Button
            size="sm"
            loading={exportAd.isPending}
            disabled={sorted.length === 0 || sorted.length > 10_000}
            startIcon={<Icon icon={Download} size="sm" />}
            onClick={runExport}
          >
            Export to Excel
          </Button>
        </div>
      </div>
      <p className={`${subText} mt-1`}>
        As Entra ID has it: a change in AD shows here after the next Entra Connect sync (about 30
        minutes) and the next HROps read (every 15 minutes).{" "}
        <Link className="underline" to="/help/org">
          How it works
        </Link>
      </p>
      {rereadFailed ? (
        <Alert tone="warning" className="mt-3">
          Could not re-read just now; showing Entra ID as of {asOf(snap.checked_at)}.
        </Alert>
      ) : null}
      {exportAd.error ? (
        <Alert tone="danger" title="Not exported" className="mt-3">
          {errorMessage(exportAd.error, "The workbook could not be built.")}
        </Alert>
      ) : null}
      {noArea ? (
        <Alert tone="info" className="mt-3">
          This snapshot carries no Area values: it was read before HROps took them from Entra ID (4
          Nov 2026). The next read fills them in.
        </Alert>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          className="w-72"
          placeholder="Search name, UPN, title, city…"
          aria-label="Search"
          startSlot={<Icon icon={Search} size="sm" />}
          ref={searchBox}
          value={qInput}
          onChange={(e) => {
            const text = e.target.value;
            setQInput(text);
            update((n) => {
              if (text) {
                n.set("q", text);
              } else {
                n.delete("q");
              }
            });
          }}
        />
        {FACETS.map((f) => (
          <FacetFilter
            key={f.key}
            label={f.label}
            selected={st.filters[f.key]}
            counts={options[f.key]}
            labelOf={(v) => labelOf(f.key, v)}
            searchExtra={
              f.key === "supervisor"
                ? (v) => byId.get(v)?.upn ?? ""
                : (v) => fold(labelOf(f.key, v))
            }
            onChange={(values) => setList(f.key, values)}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <ToggleGroup.Root
          appearance="segmented"
          size="sm"
          aria-label="Scope"
          value={[st.scope]}
          onValueChange={(v) => {
            // Pressing the pressed segment again empties the group; keep it.
            const next = v[0];
            if (next) {
              update((n) => {
                if (next === "people") {
                  n.delete("scope");
                } else {
                  n.set("scope", next);
                }
              });
            }
          }}
        >
          {SCOPES.map((s) => (
            <Toggle key={s.id} value={s.id}>
              {s.label}
            </Toggle>
          ))}
        </ToggleGroup.Root>
        <Toggle
          size="sm"
          shape="pill"
          pressed={st.gaps}
          onPressedChange={(on) =>
            update((n) => {
              if (on) {
                n.set("gaps", "1");
              } else {
                n.delete("gaps");
              }
            })
          }
        >
          Gaps only
        </Toggle>
        <FilterSelect
          size="sm"
          aria-label="Columns"
          options={COLUMNS.map((c) => ({ value: c.id, label: c.label, disabled: c.id === "name" }))}
          value={visible}
          searchable={false}
          summary={() => "Columns"}
          onValueChange={(v) =>
            setHidden(COLUMNS.map((c) => c.id).filter((id) => id !== "name" && !v.includes(id)))
          }
        />
        <Button size="sm" variant="ghost" tone="neutral" disabled={!resettable} onClick={reset}>
          Reset
        </Button>
      </div>

      <p className="text-fg mt-3 text-sm" aria-live="polite">
        {fmt(sorted.length)} of {fmt(base.length)} {st.scope === "all" ? "accounts" : "people"}
        {st.gaps ? " with a gap" : ""}
      </p>

      {sorted.length === 0 ? (
        <EmptyState
          size="sm"
          className="mt-2"
          title="Nobody matches"
          description="Change the search or a filter, or start again."
          actions={
            resettable ? (
              <Button size="sm" variant="outline" onClick={reset}>
                Reset
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          {/* A mouse shortcut, not a widget: the Name button in each row is the keyboard route. */}
          <div className="mt-2 [&_tbody_tr]:cursor-pointer" onClick={onGridClick}>
            <DataGrid
              aria-label="Active Directory"
              density="compact"
              columns={columns}
              data={pageRows}
              getRowId={(r) => r.id}
              hiddenColumnIds={hidden}
              sort={st.sort}
              onSortChange={(next) =>
                update((n) => {
                  if (
                    !next ||
                    (next.columnId === DEFAULT_SORT.columnId &&
                      next.direction === DEFAULT_SORT.direction)
                  ) {
                    n.delete("sort");
                  } else {
                    n.set("sort", `${next.columnId}:${next.direction}`);
                  }
                })
              }
            />
          </div>
          {range.pageCount > 1 ? (
            <Pagination
              className="mt-3"
              page={range.page}
              total={sorted.length}
              pageSize={PAGE_SIZE}
              onPageChange={(page) => {
                update((n) => {
                  if (page <= 1) {
                    n.delete("page");
                  } else {
                    n.set("page", String(page));
                  }
                }, true);
                top.current?.scrollIntoView({ block: "start" });
              }}
            />
          ) : null}
        </>
      )}

      <PersonDrawer
        open={drawerOpen}
        person={person}
        byId={byId}
        reports={reports}
        asOf={asOf(snap.checked_at)}
        mayReport={mayReport}
        onPick={setSelected}
        onOpenChange={setDrawerOpen}
      />
    </section>
  );
}

/** One multi-select filter, with a count on every option and "(empty)" first. */
function FacetFilter({
  label,
  selected,
  counts,
  labelOf,
  searchExtra,
  onChange,
}: {
  label: string;
  selected: string[];
  counts: Map<string, number>;
  labelOf: (v: string) => string;
  /** More text the option search should match: the UPN, or the label unaccented. */
  searchExtra: (v: string) => string;
  onChange: (values: string[]) => void;
}) {
  // Not memoised: `counts` is new whenever the list changes, which is every
  // time this would be worth caching.
  const options: FilterSelectOption[] = [...counts.entries()]
    .map(([value, n]) => ({ value, n, text: labelOf(value) }))
    .sort((a, b) => (a.value === EMPTY ? -1 : b.value === EMPTY ? 1 : a.text.localeCompare(b.text)))
    .map(({ value, n, text }) => ({
      value,
      searchText: `${text} ${searchExtra(value)}`,
      label: (
        <span className="flex w-full items-baseline justify-between gap-4">
          <span>{text}</span>
          <span className="text-fg-muted text-xs tabular-nums">{fmt(n)}</span>
        </span>
      ),
    }));
  return (
    <FilterSelect
      size="sm"
      aria-label={label}
      placeholder={label}
      searchable
      searchPlaceholder={`Find a ${label.toLowerCase()}…`}
      options={options}
      value={selected}
      summary={(sel) => (sel.length === 1 ? `${label}: ${labelOf(sel[0] ?? "")}` : label)}
      onValueChange={onChange}
    />
  );
}

function SupervisorCell({ row }: { row: Row }) {
  if (!row.p.manager_id) {
    return <span className={subText}>none</span>;
  }
  if (!row.sup) {
    return <span className={subText}>unknown account</span>;
  }
  return (
    <div>
      <span className="flex items-center gap-1.5">
        {nameOf(row.sup)}
        {row.sup.account_enabled === false ? (
          <Badge tone="warning" variant="subtle" size="xs">
            disabled
          </Badge>
        ) : null}
      </span>
      <div className={subText}>{row.sup.upn}</div>
    </div>
  );
}

// ─── the drawer ──────────────────────────────────────────────────────────────

/**
 * One person, from the same snapshot: their fields, their supervisor and
 * their direct reports, both clickable to move to that person. Two links out,
 * and no control that changes anything.
 *
 * `open` is held apart from `person` so the content stays put while the
 * drawer slides out.
 */
function PersonDrawer({
  open,
  person,
  byId,
  reports,
  asOf: when,
  mayReport,
  onPick,
  onOpenChange,
}: {
  open: boolean;
  person: OrgPerson | undefined;
  byId: Map<string, OrgPerson>;
  reports: Map<string, OrgPerson[]>;
  asOf: string;
  mayReport: boolean;
  onPick: (id: string) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const sup = person?.manager_id ? byId.get(person.manager_id) : undefined;
  const direct = [...((person && reports.get(person.id)) ?? [])].sort((a, b) =>
    nameOf(a).localeCompare(nameOf(b)),
  );
  // The chart draws enabled accounts and any disabled one somebody still
  // reports to (routes_org._chartable); anyone else has no box to centre on.
  const inChart =
    !!person && (person.account_enabled !== false || direct.some((r) => r.account_enabled));
  const value = (v: string | null | undefined): ReactNode =>
    blank(v) ? <span className="text-fg-muted">—</span> : v;
  const personLink = (p: OrgPerson) => (
    <button type="button" className="text-left underline" onClick={() => onPick(p.id)}>
      {nameOf(p)}
    </button>
  );

  return (
    <Drawer.Root open={open && !!person} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup>
            {person ? (
              <>
                <Drawer.Header>
                  <Drawer.Title>{nameOf(person)}</Drawer.Title>
                  <Drawer.CloseButton />
                </Drawer.Header>
                <Drawer.Body className="space-y-5">
                  <Drawer.Description>{person.upn}</Drawer.Description>
                  <DescriptionList
                    items={[
                      {
                        label: "Supervisor",
                        value: !person.manager_id ? (
                          <span className="text-fg-muted">none</span>
                        ) : sup ? (
                          <span className="flex flex-wrap items-center gap-1.5">
                            {personLink(sup)}
                            {sup.account_enabled === false ? (
                              <Badge tone="warning" variant="subtle" size="xs">
                                disabled
                              </Badge>
                            ) : null}
                            <span className={`${subText} w-full`}>{sup.upn}</span>
                          </span>
                        ) : (
                          <span className="text-fg-muted">unknown account</span>
                        ),
                      },
                      { label: "Job title / function", value: value(person.job_title) },
                      { label: "Department", value: value(person.department) },
                      { label: "Area (business unit)", value: value(person.business_category) },
                      { label: "Country", value: value(person.country) },
                      { label: "City", value: value(person.city) },
                      { label: "Legal entity", value: value(person.company_name) },
                      { label: "Office (brand)", value: value(person.office_location) },
                      {
                        label: "Account",
                        value: person.account_enabled === false ? "Disabled" : "Enabled",
                      },
                      { label: "Source", value: person.synced ? "AD-synced" : "Cloud-only" },
                      ...(person.person === false
                        ? [{ label: "Kind", value: "Mailbox or service account" }]
                        : []),
                    ]}
                  />
                  <div>
                    <h3 className="text-fg text-sm font-semibold">
                      Direct reports ({fmt(direct.length)})
                    </h3>
                    {direct.length ? (
                      <ul className="mt-1 space-y-1 text-sm">
                        {direct.map((r) => (
                          <li key={r.id} className="flex flex-wrap items-baseline gap-x-2">
                            {personLink(r)}
                            {r.account_enabled === false ? (
                              <Badge tone="warning" variant="subtle" size="xs">
                                disabled
                              </Badge>
                            ) : null}
                            <span className={subText}>{r.job_title}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`${subText} mt-1`}>Nobody reports to this account.</p>
                    )}
                  </div>
                  <p className={subText}>
                    Read-only copy of Entra ID as of {when}. A wrong value is corrected through
                    Workforce, Report a change, which is approved there and written to AD.
                    {mayReport ? "" : " That page needs the HROps.Workforce role."}
                  </p>
                </Drawer.Body>
                <Drawer.Footer className="flex flex-wrap gap-2">
                  {inChart ? (
                    <Link
                      className={`${control} hover:bg-bg-subtle`}
                      to={`/org?view=directory&root=${encodeURIComponent(person.id)}`}
                    >
                      Show in chart
                    </Link>
                  ) : null}
                  {mayReport && person.upn && person.person !== false ? (
                    <Link className={`${control} hover:bg-bg-subtle`} to={reportLink(person)}>
                      Report a change
                    </Link>
                  ) : null}
                </Drawer.Footer>
              </>
            ) : null}
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
